import { PageController } from '@client/components/_core/layout/PageController';
import { DndContainer } from '@client/components/_ui/data-display/dnd/DndContainer';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import {
    OutlinePanel,
    type OutlineSectionNode,
} from '@client/components/compliance-audit-templates/outline/OutlinePanel';
import {
    agentApplicableFieldCount,
    agentFillCount,
    isRoutingStep,
} from '@client/components/compliance-audit-templates/structure/agent-completeness';
import { CompletenessDot } from '@client/components/compliance-audit-templates/structure/CompletenessIndicators';
import { GeneralConfigurationCard } from '@client/components/compliance-audit-templates/structure/GeneralConfigurationCard';
import { SectionBlock } from '@client/components/compliance-audit-templates/structure/SectionBlock';
import { stepAnchorId } from '@client/components/compliance-audit-templates/structure/StepCard';
import {
    type ComplianceAuditTemplateStructureFormData,
    complianceAuditTemplateStructureFormStore,
    type StructureSectionFormData,
    type StructureStepFormData,
    type StructureTreeSection,
} from '@client/components/compliance-audit-templates/structure/structure-store';
import {
    surfaceCreatedNewDraft,
    useStructureMutations,
} from '@client/components/compliance-audit-templates/structure/useStructureMutations';
import { normalizeWorkedExamples } from '@client/components/compliance-audit-templates/structure/worked-examples-fields';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { trpc } from '@client/trpc/client';
import { parseComplianceAuditTemplateCheckExamples } from '@shared/types';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import { Empty } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const Route = createFileRoute(
    '/admin-console/platform-content/compliance-audit-templates/details/$complianceAuditTemplateId/structure',
)({
    staticData: {
        ui: { useParentTitle: false },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <StructureEditor />
        </PageController>
    );
}

// A V-field save arrives as an object keyed by the changed index
// (`{ sections: { '2': { steps: { '0': { title } } } } }`) — never as an array.
type IndexedChange<T> = Partial<
    Record<string, Omit<Partial<T>, 'steps'> & { steps?: Partial<Record<string, Partial<StructureStepFormData>>> }>
>;

function collectLeafPaths(value: unknown, parentPath = ''): string[] {
    if (value === null || typeof value !== 'object') {
        return parentPath ? [parentPath] : [];
    }

    const entries = Object.entries(value);
    if (entries.length === 0) {
        return parentPath ? [parentPath] : [];
    }

    return entries.flatMap(([key, child]) => collectLeafPaths(child, parentPath ? `${parentPath}.${key}` : key));
}

/**
 * CUR-424 structure redesign — the Structure tab as a two-panel editor: the
 * sticky outline panel (navigation, search-dim, completeness dots) beside the
 * content column of section cards. Inline fields keep flowing through the
 * structure form store, whose `onSave` inspects the changed path and
 * dispatches to the section or step update procedure; add/delete/reorder and
 * the sub-step drawer mutate through the dedicated structure procedures
 * (design D1). The panel-sync state — collapsed steps, collapsed outline
 * sections, selection, search — is purely ephemeral React state lifted here.
 */
function StructureEditor() {
    const { complianceAuditTemplateId } = Route.useParams();
    // `version` = read-only snapshot viewing, validated by the details route.
    const { version } = useSearch({
        from: '/admin-console/platform-content/compliance-audit-templates/details/$complianceAuditTemplateId',
    });
    const { trpcUtils } = useTrpc();

    const getTemplateQuery = trpc.admin.complianceAuditTemplate.getComplianceAuditTemplateVersion.useQuery({
        complianceAuditTemplateId,
        version,
    });
    const updateVersionMutation = trpc.admin.complianceAuditTemplate.updateComplianceAuditTemplateVersion.useMutation();
    const updateSectionMutation = trpc.admin.complianceAuditTemplate.updateComplianceAuditTemplateSection.useMutation();
    const updateStepMutation = trpc.admin.complianceAuditTemplate.updateComplianceAuditTemplateStep.useMutation();
    const mutations = useStructureMutations(complianceAuditTemplateId, version);

    const template = getTemplateQuery.data;
    const workingVersion = template?.workingVersion;
    const sections = workingVersion?.Sections;

    // Ephemeral editor state (design D8) — keyed by row ids so it survives
    // refetches; resets on reload by design. Everything starts expanded: an
    // empty `collapsedStepIds` means every card is open, and a step that is
    // not in the set yet (freshly added) is open by default.
    const [collapsedStepIds, setCollapsedStepIds] = useState<ReadonlySet<string>>(new Set());
    const [collapsedSectionIds, setCollapsedSectionIds] = useState<ReadonlySet<string>>(new Set());
    const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
    const [flashStepId, setFlashStepId] = useState<string | null>(null);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (!flashStepId) {
            return;
        }
        const timeout = setTimeout(() => setFlashStepId(null), 1600);
        return () => clearTimeout(timeout);
    }, [flashStepId]);

    const toggleStep = useCallback((stepId: string) => {
        setCollapsedStepIds((prev) => {
            const next = new Set(prev);
            if (next.has(stepId)) {
                next.delete(stepId);
            } else {
                next.add(stepId);
            }
            return next;
        });
    }, []);

    const toggleSection = useCallback((sectionId: string) => {
        setCollapsedSectionIds((prev) => {
            const next = new Set(prev);
            if (next.has(sectionId)) {
                next.delete(sectionId);
            } else {
                next.add(sectionId);
            }
            return next;
        });
    }, []);

    const expandSection = useCallback((sectionId: string) => {
        setCollapsedSectionIds((prev) => {
            if (!prev.has(sectionId)) {
                return prev;
            }
            const next = new Set(prev);
            next.delete(sectionId);
            return next;
        });
    }, []);

    const selectStep = useCallback((stepId: string) => {
        setSelectedStepId(stepId);
        setCollapsedStepIds((prev) => {
            if (!prev.has(stepId)) {
                return prev;
            }
            const next = new Set(prev);
            next.delete(stepId);
            return next;
        });
        setFlashStepId(stepId);
        // Wait a frame so a just-expanded card has laid out before scrolling to it.
        requestAnimationFrame(() => {
            document.getElementById(stepAnchorId(stepId))?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }, []);

    const expandAll = useCallback(() => {
        setCollapsedSectionIds(new Set());
        setCollapsedStepIds(new Set());
    }, []);

    // Collapse-all folds every step card and every outline section together.
    const collapseAll = useCallback(() => {
        setCollapsedSectionIds(new Set(sections?.map((section) => String(section.id))));
        setCollapsedStepIds(new Set(sections?.flatMap((section) => section.Steps.map((step) => String(step.id)))));
    }, [sections]);

    const addSection = useCallback(() => void mutations.createSection({ name: 'New section' }), [mutations]);

    const addStep = useCallback(
        async (section: StructureTreeSection) => {
            const result = await mutations.createStep({
                complianceAuditTemplateSectionId: section.id,
                title: 'New step',
                agentWhatItChecks: '',
                whyItMatters: '',
                agentDecisionRule: '',
                // `appliesWhen` omitted — the input schema defaults it to alwaysApply.
            });
            // The new card appears selected — and expanded, since a step outside
            // `collapsedStepIds` is open by default.
            if (result?.step) {
                setSelectedStepId(String(result.step.id));
            }
        },
        [mutations],
    );

    // Steps number continuously across sections ("1", "2", … then "3" in the
    // next section) — computed at render time, never stored.
    const stepNumberStarts = useMemo(
        () =>
            (sections ?? []).reduce<number[]>((starts, _section, index, all) => {
                starts.push(index === 0 ? 1 : (starts[index - 1] ?? 1) + (all[index - 1]?.Steps.length ?? 0));
                return starts;
            }, []),
        [sections],
    );

    // The outline's view of the tree: short titles, global numbers, and the
    // agent-completeness dot in the trailing slot.
    const outlineNodes = useMemo<OutlineSectionNode[]>(
        () =>
            (sections ?? []).map((section, sectionIndex) => ({
                id: String(section.id),
                label: section.name.trim() || 'Untitled section',
                steps: section.Steps.map((step, stepIndex) => {
                    const isRouting = isRoutingStep(step.SubSteps);
                    return {
                        id: String(step.id),
                        number: (stepNumberStarts[sectionIndex] ?? 1) + stepIndex,
                        label: step.shortTitle?.trim() || step.title.trim() || 'Untitled step',
                        searchText: `${step.title} ${step.shortTitle ?? ''}`,
                        trailing: (
                            <CompletenessDot
                                count={agentFillCount(step, isRouting)}
                                applicable={agentApplicableFieldCount(isRouting)}
                            />
                        ),
                    };
                }),
            })),
        [sections, stepNumberStarts],
    );

    // Memoised — `useInit` re-initialises whenever the `data` reference changes.
    const formData = useMemo<ComplianceAuditTemplateStructureFormData | undefined>(
        () =>
            workingVersion
                ? {
                      evidenceRetrievalMode: workingVersion.evidenceRetrievalMode,
                      sections: workingVersion.Sections.map(
                          (section): StructureSectionFormData => ({
                              id: section.id,
                              name: section.name,
                              description: section.description,
                              steps: section.Steps.map(
                                  (step): StructureStepFormData => ({
                                      id: step.id,
                                      title: step.title,
                                      shortTitle: step.shortTitle,
                                      whyItMatters: step.whyItMatters,
                                      agentWhatItChecks: step.agentWhatItChecks,
                                      agentDecisionRule: step.agentDecisionRule,
                                      passWhen: step.passWhen,
                                      failWhen: step.failWhen,
                                      queryWhen: step.queryWhen,
                                      notApplicableWhen: step.notApplicableWhen,
                                      flagWhen: step.flagWhen,
                                      appliesWhen: step.appliesWhen,
                                      agentExamples:
                                          parseComplianceAuditTemplateCheckExamples(step.agentExamples) ?? [],
                                      score: step.score,
                                      notesForEditor: step.notesForEditor,
                                  }),
                              ),
                          }),
                      ),
                  }
                : undefined,
        [workingVersion],
    );

    complianceAuditTemplateStructureFormStore.useInit({
        entityLabel: 'Audit structure',
        data: formData,
        isLoading: getTemplateQuery.isLoading,
        // Snapshot views are read-only everywhere the store reaches (design D1).
        isReadOnly: (template?.isArchived ?? false) || (template?.isSnapshot ?? false),

        async onReload() {
            await getTemplateQuery.refetch();
        },

        // Changed-path procedure routing (design D2): the version-wide general
        // configuration → updateVersion; one changed section index →
        // updateSection; a nested `steps` index → updateStep.
        async onSave(currentFormData, changedValues) {
            try {
                // Version-wide field — no section or step index is involved, so
                // it short-circuits the tree-path routing below.
                if (changedValues.evidenceRetrievalMode !== undefined) {
                    const result = await updateVersionMutation.mutateAsync({
                        complianceAuditTemplateId,
                        selectedVersion: version,
                        evidenceRetrievalMode: changedValues.evidenceRetrievalMode,
                    });
                    surfaceCreatedNewDraft(result);

                    await trpcUtils.admin.complianceAuditTemplate.invalidate();

                    return { success: true };
                }

                // Name the offending payload in the error so a save-shape bug is
                // diagnosable from the modal alone (CUR-538).
                const unsupportedShapeError = () =>
                    new Error(
                        `Unsupported structure save shape — changed field(s): ${
                            collectLeafPaths(changedValues).join(', ') || '(none)'
                        }`,
                    );

                const sectionsChange = changedValues.sections as unknown as
                    | IndexedChange<StructureSectionFormData>
                    | undefined;
                const sectionKey = sectionsChange && !Array.isArray(sectionsChange) && Object.keys(sectionsChange)[0];
                const sectionChange = sectionKey ? sectionsChange?.[sectionKey] : undefined;
                const section = sectionKey ? currentFormData.sections[Number(sectionKey)] : undefined;

                if (!sectionChange || !section) {
                    return { success: false, error: unsupportedShapeError() };
                }

                const stepsChange = sectionChange.steps;
                const stepKey = stepsChange && !Array.isArray(stepsChange) && Object.keys(stepsChange)[0];

                if (stepKey) {
                    const stepChange = stepsChange?.[stepKey];
                    const step = section.steps[Number(stepKey)];
                    if (!stepChange || !step) {
                        return { success: false, error: unsupportedShapeError() };
                    }

                    const result = await updateStepMutation.mutateAsync({
                        complianceAuditTemplateId,
                        selectedVersion: version,
                        complianceAuditTemplateStepId: step.id,
                        set: {
                            // Non-nullable text columns take '' when the field is cleared.
                            ...(stepChange.title !== undefined && { title: stepChange.title || 'Untitled step' }),
                            ...(stepChange.shortTitle !== undefined && { shortTitle: stepChange.shortTitle }),
                            ...(stepChange.whyItMatters !== undefined && {
                                whyItMatters: stepChange.whyItMatters ?? '',
                            }),
                            ...(stepChange.agentWhatItChecks !== undefined && {
                                agentWhatItChecks: stepChange.agentWhatItChecks ?? '',
                            }),
                            ...(stepChange.agentDecisionRule !== undefined && {
                                agentDecisionRule: stepChange.agentDecisionRule ?? '',
                            }),
                            ...(stepChange.passWhen !== undefined && { passWhen: stepChange.passWhen }),
                            ...(stepChange.failWhen !== undefined && { failWhen: stepChange.failWhen }),
                            ...(stepChange.queryWhen !== undefined && { queryWhen: stepChange.queryWhen }),
                            ...(stepChange.notApplicableWhen !== undefined && {
                                notApplicableWhen: stepChange.notApplicableWhen,
                            }),
                            ...(stepChange.flagWhen !== undefined && { flagWhen: stepChange.flagWhen }),
                            ...(stepChange.appliesWhen !== undefined && {
                                appliesWhen: stepChange.appliesWhen,
                            }),
                            ...(stepChange.agentExamples !== undefined && {
                                agentExamples: stepChange.agentExamples?.length
                                    ? normalizeWorkedExamples(stepChange.agentExamples)
                                    : null,
                            }),
                            ...(stepChange.score !== undefined && { score: stepChange.score }),
                            ...(stepChange.notesForEditor !== undefined && {
                                notesForEditor: stepChange.notesForEditor,
                            }),
                        },
                    });
                    surfaceCreatedNewDraft(result);
                } else {
                    const result = await updateSectionMutation.mutateAsync({
                        complianceAuditTemplateId,
                        selectedVersion: version,
                        complianceAuditTemplateSectionId: section.id,
                        set: {
                            ...(sectionChange.name !== undefined && { name: sectionChange.name || 'Untitled section' }),
                            ...(sectionChange.description !== undefined && {
                                description: sectionChange.description,
                            }),
                        },
                    });
                    surfaceCreatedNewDraft(result);
                }

                await trpcUtils.admin.complianceAuditTemplate.invalidate();

                return { success: true };
            } catch (error) {
                return { success: false, error };
            }
        },
    });

    if (getTemplateQuery.isPending) {
        return <Loading />;
    }

    if (getTemplateQuery.error) {
        return (
            <InlineQueryError
                error={getTemplateQuery.error}
                onRetry={() => void getTemplateQuery.refetch()}
                title="Couldn't load the audit structure"
            />
        );
    }

    if (!template || !sections || !formData) {
        return null;
    }

    const isReadOnly = template.isArchived || template.isSnapshot;
    const stepCount = sections.reduce((total, section) => total + section.Steps.length, 0);

    return (
        <div className="flex items-start gap-4">
            <OutlinePanel
                header={`${sections.length} sections · ${stepCount} steps`}
                nodes={outlineNodes}
                search={search}
                onSearchChange={setSearch}
                collapsedSectionIds={collapsedSectionIds}
                onToggleSection={toggleSection}
                onExpandSection={expandSection}
                selectedStepId={selectedStepId}
                onSelectStep={selectStep}
                isAllExpanded={stepCount > 0 ? collapsedStepIds.size === 0 : undefined}
                onExpandAll={expandAll}
                onCollapseAll={collapseAll}
                dnd={
                    isReadOnly
                        ? undefined
                        : {
                              onReorderSections: (orderedIds) =>
                                  void mutations.reorderStructure({
                                      level: 'SECTION',
                                      orderedIds: orderedIds.map((id) => BigInt(id)),
                                  }),
                              onReorderSteps: (sectionId, orderedIds) =>
                                  void mutations.reorderStructure({
                                      level: 'STEP',
                                      parentId: BigInt(sectionId),
                                      orderedIds: orderedIds.map((id) => BigInt(id)),
                                  }),
                          }
                }
                footer={
                    !isReadOnly && (
                        <Button.Add
                            variant="dashed"
                            size="small"
                            label="Add section"
                            className="w-full"
                            onClick={addSection}
                        />
                    )
                }
            />

            <div className="min-w-0 flex-1">
                <GeneralConfigurationCard />

                {sections.length === 0 && (
                    <Empty description="No sections yet — the audit structure starts with a section of steps" />
                )}

                <DndContainer
                    ids={sections.map((section) => String(section.id))}
                    onDragEnd={(keys) =>
                        void mutations.reorderStructure({
                            level: 'SECTION',
                            orderedIds: keys.map((key) => BigInt(key)),
                        })
                    }
                    renderItem={({ index, DragButton }) => {
                        const section = sections[index];
                        if (!section) {
                            return null;
                        }
                        return (
                            <SectionBlock
                                key={String(section.id)}
                                sections={sections}
                                section={section}
                                sectionIndex={index}
                                stepNumberStart={stepNumberStarts[index] ?? 1}
                                complianceAuditTemplateId={complianceAuditTemplateId}
                                version={version}
                                mutations={mutations}
                                isReadOnly={isReadOnly}
                                DragButton={DragButton}
                                onAddStep={addStep}
                                collapsedStepIds={collapsedStepIds}
                                flashStepId={flashStepId}
                                onToggleStep={toggleStep}
                            />
                        );
                    }}
                />
            </div>
        </div>
    );
}
